import { theme } from "config";
import { observer } from "mobx-react";
import { Course, Step } from "models";
import * as React from "react";
import styled from "styled-components";
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { StepType } from "types";
import { ConfirmDialog, Icon } from "components";

const placeholder_image = require('assets/images/Placeholder_img.png');

export interface IGridCardProps {
  course: Course,
  step?:  Step,
  idx?:   number,
  edit?:  boolean,
}

export const GridCard = observer((props: IGridCardProps) => {
  const { step, idx, course, edit } = props;
  const { t }    = useTranslation();

  const isNotMobileView = useMediaQuery(theme.breakpoints.up('sm'));

  const [showConfirmDialog, setShowConfirmDialog] = React.useState<boolean>(false);

  React.useEffect(() => {
    step?.getThumbnail?.getURL();
  }, [step, step?.getThumbnail])

  return(
    <>
      <Container 
        edit     = {edit}
        onClick  = {() => !edit && step.course.setCurStep(step)}
        mobile   = {!isNotMobileView}
      >
        <BGImg src={
            step?.thumbnail 
              ? step?.thumbnail?.file_path
              : (edit ? placeholder_image : undefined)
          } 
          edit={edit} 
        />
        {edit && 
          <List>
            {step 
              ? 
                <>
                  <ListButton 
                    label   = {t('edit_card')}
                    onClick = {() => step.course.setCurStep(step)}
                    icon    = {
                      <EditIcon
                        sx        = {{ fontSize: '1.4rem' }}
                        cursor    = 'pointer'
                        htmlColor = {theme.palette.black.main}
                      />
                    }
                  />
                  <ListButton 
                    label   = {t('remove_card')}
                    onClick = {() => setShowConfirmDialog(true)}
                    icon    = {
                      <DeleteIcon
                        sx        = {{ fontSize: '1.4rem' }}
                        cursor    = 'pointer'
                        htmlColor = {theme.palette.black.main}
                      />
                    }
                  />
                </>
              :
                <>
                  <ListButton label={t('add_text')}  onClick={() => course.getCurStep.addGridStep(StepType.Read)}/>
                  <ListButton label={t('add_video')} onClick={() => course.getCurStep.addGridStep(StepType.Video)}/>
                </>
            }
          </List>
        }
        {!edit && 
          <PlayIconContainer style={{padding: step?.isVideo ? undefined : "6px"}}>
            {step?.isVideo 
              ? <PlayArrowRoundedIcon 
                  style={{ width: "36px", height: "36px", color: "#00AB84" }}
                />
              : <Icon size="size-24" color="#00AB84" iconId='read' />
            }
          </PlayIconContainer>
        }
      </Container>
      <ConfirmDialog 
        open              = {showConfirmDialog}
        onClose           = {() => setShowConfirmDialog(false)}
        title             = {t('confirm_remove_grid_card_title') + '?'}
        content           = {t('confirm_remove_grid_card_message')}
        confirmButtonText = {t('confirm')}
        cancelButtonText  = {t('cancel')}
        onCancel          = {() => setShowConfirmDialog(false)}
        onConfirm         = {async () => {
          try {
            step.getParent.removeGridStep(idx);
            setShowConfirmDialog(false);
          } catch(err) {
            console.error("Failed to remove grid card", err);
          }
        }}
      />
    </>
  );
});

interface IListButtonProps {
  icon?:    React.ReactNode,
  label?:   string,
  onClick?: React.MouseEventHandler<HTMLDivElement>,
}

const ListButton = (props: IListButtonProps) => {
  return (
    <ListItem disablePadding>
      <ListItemButton onClick={props.onClick}>
        <ListItemIcon>
          {props.icon || 
            <AddIcon
              sx        = {{ fontSize: '1.4rem' }}
              cursor    = 'pointer'
              htmlColor = {theme.palette.black.main}
            />
          }
        </ListItemIcon>
        <ListItemText>
          <Typography
            sx            = {{ cursor: 'pointer', paddingLeft: 1 }}
            fontWeight    = {theme.typography.fontWeightBold}
            variant       = 'subtitle2'
            textTransform = 'uppercase'
            color         = {theme.palette.black.main}
          >
            {props.label || ""}
          </Typography>
        </ListItemText>
      </ListItemButton>
    </ListItem>
  )
}
//356 x 267 in desktop and 327 x 245 i mobile
const Container = styled.div<{edit: boolean, mobile: boolean}>`
  position:         relative;
  max-height:       ${props => props.mobile ? "245px" : '245px'};
  max-width:        ${props => props.mobile ? "327px" : '356px'};
  aspect-ratio:     1.33333;
  background:       white;
  display:          flex;
  align-items:      center;
  justify-content:  center;
  border-radius:    0.75rem;
  cursor:           ${props => props.edit ? undefined : 'pointer'};
  :hover {
    box-shadow: 2px 2px 8px rgba(39,39,39,0.15);
  }
`;

const BGImg = styled.img<{edit: boolean}>`
  width:         100%;
  height:        100%;
  opacity:       ${props => props.edit ? 0.2 : 1};
  object-fit:    cover;
  position:      absolute;
  border-radius: 0.75rem;
  background:    white;
`;

const PlayIconContainer = styled.div`
  background:      white;
  position:        absolute;
  bottom:          1rem;
  right:           1rem;
  z-index:         10;
  display:         flex;
  align-items:     center;
  justify-content: center;
  border-radius:   50%;
  box-shadow: 0px 0px 4px rgba(39,39,39,0.15);
`;